import serve from '@/serve'
import {
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref
} from '@vue/composition-api'
import AgoraRTC, {
  IAgoraRTCClient,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteAudioTrack,
  RemoteStreamType
} from 'agora-rtc-sdk-ng'
import { Message } from 'element-ui'
import { useI18n } from 'vue-i18n-composable'

import styles from '../../index.module.scss'
import Camera from './camera'
import SettingDialog from './setting-dialog'

const micImg = require('@/assets/images/room/mic.png')

const muteImg = require('@/assets/images/room/mute.png')
const unmuteImg = require('@/assets/images/room/unmute.png')

const camImg = require('@/assets/images/room/cam.png')
const noCamImg = require('@/assets/images/room/no-cam.png')

const settingsImg = require('@/assets/images/room/settings.png')
const leaveImg = require('@/assets/images/room/leave.png')

const nameImg = require('@/assets/images/room/name.png')

const ControlItem = defineComponent({
  props: {
    label: String,
    icon: String,
    onClick: Function
  },
  setup(props, { emit }) {
    const handleClick = () => {
      emit('click')
    }
    return () => (
      <div class={styles.controlItem} onClick={handleClick}>
        <img src={props.icon} />
        <div>{props.label}</div>
      </div>
    )
  }
})

export default defineComponent({
  props: ['onJoinFail'],
  setup(_props, { root, emit }) {
    const route = root.$route
    const mqtt = (root as any).$mqtt
    const { t } = useI18n()

    const deviceList = reactive<{
      videoDevices: MediaDeviceInfo[]
      micDevices: MediaDeviceInfo[]
      speakerDevices: MediaDeviceInfo[]
    }>({
      videoDevices: [],
      micDevices: [],
      speakerDevices: []
    })

    const localVolumeLevel = ref(0)

    const localVideoTrack = ref<ICameraVideoTrack>()
    const localAudioTrack = ref<IMicrophoneAudioTrack>()

    const audioTrackList = ref<IRemoteAudioTrack[]>([])

    const client = ref<IAgoraRTCClient>()

    // ─────────── 诊断代码 START（音画不同步排查用，验证完可整段删除） ───────────
    const statsTimer = ref<number | null>(null)

    // 安全序列化：把对象转成可直接复制的 JSON 字符串（处理循环引用 / BigInt）
    const j = (obj: any) => {
      try {
        const seen = new WeakSet()
        return JSON.stringify(
          obj,
          (_k, v) => {
            if (v === undefined) return null
            if (typeof v === 'bigint') return v.toString()
            if (typeof v === 'object' && v !== null) {
              if (seen.has(v)) return '[Circular]'
              seen.add(v)
            }
            return v
          },
          2
        )
      } catch (e) {
        return '[stringify failed] ' + String(e)
      }
    }

    // hook 底层 RTCPeerConnection（Agora 不暴露 pc，靠包构造函数收集实例）
    // 目的：拿到原生 outbound-rtp 的 qualityLimitationReason —— 直接区分 cpu / bandwidth
    // 必须在 Agora 建连前执行；setup 体先于 onMounted 运行，刚好赶上
    try {
      const w = window as any
      if (!w.__pcHooked && w.RTCPeerConnection) {
        const Native = w.RTCPeerConnection
        w.__pcList = []
        const Wrapped: any = function (...rtcArgs: any[]) {
          const pc = new Native(...rtcArgs)
          try {
            w.__pcList.push(pc)
          } catch {
            /* noop */
          }
          return pc
        }
        Wrapped.prototype = Native.prototype
        w.RTCPeerConnection = Wrapped
        w.__pcHooked = true
      }
    } catch (e) {
      console.warn('[pc-hook] 安装失败，已忽略（拿不到原生发送统计）', e)
    }

    // 从底层 pc 读取原生视频发送统计（最关键：qualityLimitationReason）
    const getNativeSendStats = async () => {
      try {
        const pcs = (window as any).__pcList || []
        for (const pc of pcs) {
          let report: any
          try {
            report = await pc.getStats()
          } catch {
            continue
          }
          let out: any = null
          report.forEach((s: any) => {
            if (s.type === 'outbound-rtp' && s.kind === 'video') {
              out = {
                // cpu / bandwidth / none —— 一句话定性码率受限原因
                qualityLimitationReason: s.qualityLimitationReason,
                qualityLimitationDurations: s.qualityLimitationDurations,
                framesEncoded: s.framesEncoded,
                framesSent: s.framesSent,
                // 累计发包延迟/包数 → 平均发送队列延迟(s)，视频排队堆积时会涨
                avgSendDelay:
                  s.packetsSent && s.totalPacketSendDelay != null
                    ? +(s.totalPacketSendDelay / s.packetsSent).toFixed(4)
                    : null,
                packetsSent: s.packetsSent,
                retransmittedPacketsSent: s.retransmittedPacketsSent,
                nackCount: s.nackCount,
                pliCount: s.pliCount,
                firCount: s.firCount,
                qpSum: s.qpSum
              }
            }
          })
          if (out) return out
        }
        return { note: '(未取到 video outbound-rtp，可能 pc hook 未生效)' }
      } catch (e) {
        return { error: String(e) }
      }
    }

    const probeEncodingCapability = async (codec: string) => {
      try {
        const info = await (navigator as any).mediaCapabilities.encodingInfo({
          type: 'webrtc',
          video: {
            contentType: 'video/' + codec,
            width: 1920,
            height: 1080,
            bitrate: 2000000,
            framerate: 30
          }
        })
        return {
          supported: info.supported,
          smooth: info.smooth,
          powerEfficient: info.powerEfficient
        }
      } catch (e) {
        return { error: String(e) }
      }
    }

    const measureMainThreadLag = async () => {
      const start = performance.now()
      await new Promise((r) => setTimeout(r, 100))
      return +(performance.now() - start - 100).toFixed(1)
    }

    const dumpDiagnostics = async () => {
      try {
      // 1) UA / OS / 浏览器
      let uaHints: any = {}
      const uaData: any = (navigator as any).userAgentData
      if (uaData?.getHighEntropyValues) {
        try {
          uaHints = await uaData.getHighEntropyValues([
            'platform',
            'platformVersion',
            'architecture',
            'bitness',
            'model',
            'fullVersionList'
          ])
        } catch {}
      }

      // 2) GPU
      let gpu = 'unknown'
      try {
        const canvas = document.createElement('canvas')
        const gl =
          (canvas.getContext('webgl') as WebGLRenderingContext) ||
          (canvas.getContext('experimental-webgl') as WebGLRenderingContext)
        const ext = gl.getExtension('WEBGL_debug_renderer_info')
        if (ext) {
          gpu = gl.getParameter((ext as any).UNMASKED_RENDERER_WEBGL)
        }
      } catch {}

      // 3) AudioContext 采样率
      let audioSampleRate: any = 'unknown'
      try {
        const ctx = new AudioContext()
        audioSampleRate = ctx.sampleRate
        ctx.close()
      } catch {}

      // 4) Codec 硬件编码支持（核心信号！powerEfficient=true 即硬编）
      const [vp8, h264, vp9, av1] = await Promise.all([
        probeEncodingCapability('VP8'),
        probeEncodingCapability('H264'),
        probeEncodingCapability('VP9'),
        probeEncodingCapability('AV1')
      ])

      // 5) 设备列表
      const devices = await navigator.mediaDevices.enumerateDevices()
      const deviceTable = devices.map((d) => ({
        kind: d.kind,
        label: d.label || '(no permission)',
        deviceId: d.deviceId.slice(0, 12)
      }))

      // 6) 实际采集参数（浏览器最终给出的值，可能与请求不同）
      const videoSettings = localVideoTrack.value
        ?.getMediaStreamTrack?.()
        ?.getSettings?.()
      const audioSettings = localAudioTrack.value
        ?.getMediaStreamTrack?.()
        ?.getSettings?.()

      // 7) Agora 推流 stats
      const localV = client.value?.getLocalVideoStats?.()
      const localA = client.value?.getLocalAudioStats?.()
      const rtc = client.value?.getRTCStats?.()

      // 8) 主线程响应延迟
      const mainThreadLag = await measureMainThreadLag()

      console.group(
        '%c🩺 推流诊断报告 ' + new Date().toLocaleTimeString(),
        'color:#0a84ff;font-size:14px;font-weight:bold'
      )

      console.log('%c━━━ 系统 / 浏览器 ━━━', 'color:#888')
      console.log(
        j({
          userAgent: navigator.userAgent,
          platform: uaHints.platform,
          platformVersion: uaHints.platformVersion,
          architecture: uaHints.architecture,
          bitness: uaHints.bitness,
          cpuModel: uaHints.model || '(浏览器不暴露)',
          cpuCores: navigator.hardwareConcurrency,
          deviceMemoryGB: (navigator as any).deviceMemory,
          GPU: gpu,
          audioSampleRate
        })
      )

      console.log(
        '%c━━━ Codec 硬件编码支持（powerEfficient=true 即硬编）━━━',
        'color:#888'
      )
      console.log(j({ VP8: vp8, H264: h264, VP9: vp9, AV1: av1 }))
      console.log(
        '%c当前用的是 VP8。若 VP8.powerEfficient=false 且 H264.powerEfficient=true → 改 codec 能直接解决 CPU 瓶颈',
        'color:#e67e22'
      )

      console.log('%c━━━ 采集设备 ━━━', 'color:#888')
      console.log(j(deviceTable))

      console.log('%c━━━ 实际采集参数（浏览器返回值）━━━', 'color:#888')
      console.log('video.getSettings():', j(videoSettings))
      console.log('audio.getSettings():', j(audioSettings))

      console.log('%c━━━ Agora 推流统计 ━━━', 'color:#888')
      console.log('LocalVideoStats:', j(localV))
      console.log('LocalAudioStats:', j(localA))
      console.log('RTCStats:', j(rtc))

      console.log(
        '%c━━━ 原生发送统计（qualityLimitationReason: cpu/bandwidth/none）━━━',
        'color:#888'
      )
      console.log(j(await getNativeSendStats()))

      console.log('%c━━━ 主线程响应延迟 ━━━', 'color:#888')
      console.log(
        'mainThreadLag(ms):',
        mainThreadLag,
        mainThreadLag > 50
          ? '⚠️ 主线程压力大（编码可能在抢资源）'
          : '✅ 正常'
      )

      console.groupEnd()
      } catch (e) {
        console.warn('[dumpDiagnostics] 诊断失败，已忽略', e)
      }
    }

    const startStatsMonitor = () => {
      try {
        if (statsTimer.value) return
        statsTimer.value = window.setInterval(async () => {
          try {
            if (!client.value) return
            const v = client.value.getLocalVideoStats() as any
            const a = client.value.getLocalAudioStats() as any
            const rtc = client.value.getRTCStats()
            const lag = await measureMainThreadLag()
            const remoteV = host.value
              ? client.value.getRemoteVideoStats()[host.value.uid]
              : null
            const remoteA = host.value
              ? client.value.getRemoteAudioStats()[host.value.uid]
              : null
            const native = await getNativeSendStats()

            const captureFps = v?.captureFrameRate
            const sendFps = v?.sendFrameRate
            const fpsDelta =
              captureFps && sendFps ? +(captureFps - sendFps).toFixed(1) : null
            // 本地视频冻结占比：冻结时长 / 总时长，体感"卡/不同步"的直接来源
            const freezeRatio =
              v?.totalDuration && v?.totalFreezeTime != null
                ? +(v.totalFreezeTime / v.totalDuration).toFixed(3)
                : null

            console.log(
              '%c[stats ' + new Date().toLocaleTimeString() + '] ',
              'color:#0a84ff;font-weight:bold',
              j({
                // ── 发送侧（别人看到的"我"）──
                'video.captureFps': captureFps,
                'video.sendFps': sendFps,
                'fps差(>2即编码丢帧)': fpsDelta,
                'video.encodeDelay(ms)':
                  v?.encodeDelay ?? v?.encoderDelay ?? '(SDK未暴露)',
                'video.sendBitrate(bps)': v?.sendBitrate,
                'video.targetBitrate(bps)': v?.targetSendBitrate,
                'video.分辨率':
                  v?.sendResolutionWidth + 'x' + v?.sendResolutionHeight,
                'video.丢包率': v?.currentPacketLossRate,
                'video.冻结占比': freezeRatio,
                'audio.sendBitrate(bps)': a?.sendBitrate,
                'audio.丢包率': a?.currentPacketLossRate,
                // ── 原生发送统计（定性根因）──
                native,
                // ── 链路 ──
                'RTT(ms)': rtc?.RTT,
                上行带宽: rtc?.OutgoingAvailableBandwidth,
                '主线程lag(ms)': lag,
                // ── 接收侧（我看到的"主播"）──
                'remote.端到端延迟': remoteV?.end2EndDelay,
                'remote.video.冻结率': remoteV?.freezeRate,
                'remote.video.接收延迟': remoteV?.receiveDelay,
                'remote.video.丢包率': remoteV?.packetLossRate,
                'remote.audio.端到端延迟': remoteA?.end2EndDelay,
                'remote.audio.冻结率': remoteA?.freezeRate,
                'remote.audio.丢包率': remoteA?.packetLossRate
              })
            )
          } catch (e) {
            console.warn('[stats] 单次采样失败，已忽略', e)
          }
        }, 3000)
      } catch (e) {
        console.warn('[startStatsMonitor] 启动失败，已忽略', e)
      }
    }

    // 暴露到 window，方便随时手动跑一次
    try {
      ;(window as any).__roomDiag = dumpDiagnostics
    } catch {}
    // ─────────── 诊断代码 END ───────────

    const deviceInfo = reactive({
      cameraId: '',
      microphoneId: '',
      speakerId: '',
      definition: 1080,
      micName: 'Mic',
      rate: 16 / 9
    })

    const showSetting = ref(false)

    const isJoined = ref(false)
    const joinLoading = ref(false)
    const name = ref('')

    const host = ref()

    // 自己加入时的 uid，用于在 user-published 里跳过自己（防止 uid 回环时听到自己）
    const joinedUid = ref<string | number>()

    const openLog = () => {
      try {
        // 开启日志上传功能
        AgoraRTC.enableLogUpload();
        // 将日志输出级别设置为 INFO
        AgoraRTC.setLogLevel(1);
      } catch (e) {
        console.log('日志开启失败', e)
      }
    }

    const getDevices = () => {
      AgoraRTC.getDevices().then((devices) => {
        console.log(devices)
        deviceList.micDevices = devices.filter(function (device) {
          return device.kind === 'audioinput'
        })

        deviceList.videoDevices = devices.filter(function (device) {
          return device.kind === 'videoinput'
        })

        deviceList.speakerDevices = devices.filter(function (device) {
          return device.kind === 'audiooutput'
        })
        let defaultDevice
        try {
          defaultDevice = JSON.parse(localStorage.getItem('deviceInfo') || '{}')
        } catch {
          defaultDevice = {}
        }

        const defaultMicDevice =
          deviceList.micDevices.find(
            (device) => device.deviceId === defaultDevice.microphoneId
          ) ||
          deviceList.micDevices[0] ||
          {}

        deviceInfo.microphoneId = defaultMicDevice.deviceId
        deviceInfo.micName = defaultMicDevice.label

        deviceInfo.cameraId =
          deviceList.videoDevices.find(
            (device) => device.deviceId === defaultDevice.cameraId
          )?.deviceId || deviceList.videoDevices[0]?.deviceId

        deviceInfo.speakerId =
          deviceList.speakerDevices.find(
            (device) => device.deviceId === defaultDevice.speakerId
          )?.deviceId || deviceList.speakerDevices[0]?.deviceId

        return Promise.all([
          AgoraRTC.createCameraVideoTrack({
            cameraId: deviceInfo.cameraId,
            encoderConfig: {
              height: deviceInfo.definition,
              width: deviceInfo.definition * deviceInfo.rate
            },
            optimizationMode: 'detail' // 从detail修改为motion，保证流畅度优先，尝试修复音画不同步问题
          }),
          AgoraRTC.createMicrophoneAudioTrack({
            microphoneId: deviceInfo.microphoneId,
            // encoderConfig: "music_standard" // 当前音频轨道未显式设置 encoderConfig，使用默认值 "music_standard"。建议显式指定稳定配置，避免浏览器差异导致波动：
          })
        ]).then((res) => {
          const [videoTrack, audioTrack] = res
          localVideoTrack.value = videoTrack
          localAudioTrack.value = audioTrack
          console.log('localVideoTrack', localVideoTrack.value)
          videoTrack?.play('meVideo', {
            fit: 'contain'
          })
          setInterval(() => {
            const level = audioTrack.getVolumeLevel()
            console.log('local stream audio level', level)
            localVolumeLevel.value = level
          }, 500)
        })
      })
    }

    const switchMute = () => {
      localAudioTrack.value?.setMuted(!localAudioTrack.value.muted)
    }

    const switchCamera = () => {
      localVideoTrack.value?.setMuted(!localVideoTrack.value!.muted)
    }

    const openSetting = async () => {
      showSetting.value = true
    }

    // iOS/WebKit 同一时刻只允许一路摄像头采集，设置弹窗会另开一路预览采集，
    // 切换相机/关闭弹窗时会触发 WebKit 媒体中断，把页面上正在播放的本地与远端
    // video 元素一并暂停且不会自动恢复（安卓允许并发采集，故不复现）。
    // 弹窗关闭后主动重新 play 本地和远端拉流画面，修复黑屏。
    const replayVideos = () => {
      try {
        localVideoTrack.value?.play('meVideo', { fit: 'contain' })
      } catch (e) {
        console.warn('[recoverVideos] 本地画面恢复失败，已忽略', e)
      }
      try {
        // host.value 会被任意发布的用户（含访客 uid≠1）覆盖，这里必须和
        // user-published 里一致只认主播 uid===1，否则会把访客画面 play 进主画面容器，
        // 且 Agora play 不同 track 会追加 <video> 造成多个访客画面堆叠。
        if (host.value?.uid === 1) {
          host.value.videoTrack?.play('hostVideo', { fit: 'contain' })
        }
      } catch (e) {
        console.warn('[recoverVideos] 远端拉流画面恢复失败，已忽略', e)
      }
    }

    const isIOS = () =>
      /iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    // 关闭设置后的画面恢复：仅 iOS 需要（iOS 单摄像头限制，弹窗预览会中断主采集）。
    // 安卓允许并发采集，没有这个问题，不做处理；远端音频也不在这里碰，避免多余操作。
    const recoverVideosAfterSetting = async () => {
      if (!isIOS()) {
        return
      }
      await nextTick()
      replayVideos()
      // WebKit 的 interruptionend 在摄像头硬件释放后才触发，过早 replay 会被再次暂停，延时兜底一次。
      setTimeout(replayVideos, 500)
    }

    const handleConfirm = (device) => {
      showSetting.value = false

      // 只应用「真正变了的」设备——尤其麦克风：安卓上无谓地重新获取麦克风会重置音频会话、
      // 打断远端音频播放（表现为"听不到别人，但别人能听到你"）。
      const cameraChanged = device.cameraId !== deviceInfo.cameraId
      const micChanged = device.microphoneId !== deviceInfo.microphoneId
      const speakerChanged = device.speakerId !== deviceInfo.speakerId
      const resolutionChanged =
        device.definition !== deviceInfo.definition ||
        device.rate !== deviceInfo.rate

      deviceInfo.cameraId = device.cameraId
      deviceInfo.microphoneId = device.microphoneId
      deviceInfo.speakerId = device.speakerId
      deviceInfo.definition = device.definition
      deviceInfo.rate = device.rate
      deviceInfo.micName = device.micName

      // 摄像头：真正切换就 setDevice；没切但 iOS 需要从弹窗预览中断里恢复
      if (cameraChanged || isIOS()) {
        localVideoTrack.value?.setDevice(deviceInfo.cameraId)
      }
      if (cameraChanged || resolutionChanged) {
        localVideoTrack.value?.setEncoderConfiguration({
          height: deviceInfo.definition,
          width: deviceInfo.definition * deviceInfo.rate
        })
      }
      // 麦克风：仅真正切换时才 setDevice（安卓重获麦克风会打断远端音频）
      if (micChanged) {
        localAudioTrack.value?.setDevice(deviceInfo.microphoneId)
      }
      // 扬声器：仅真正切换时切（switchSpeaker 内部已对不支持的平台跳过）
      if (speakerChanged) {
        switchSpeaker(deviceInfo.speakerId)
      }

      recoverVideosAfterSetting()
    }

    // 取消：不改任何设备。仅 iOS 需要恢复被弹窗预览中断的摄像头；
    // 安卓/桌面什么都不做，尤其不碰麦克风/扬声器，避免打断安卓远端音频。
    const handleCancelSetting = () => {
      showSetting.value = false
      if (isIOS()) {
        localVideoTrack.value?.setDevice(deviceInfo.cameraId)
        localVideoTrack.value?.setEncoderConfiguration({
          height: deviceInfo.definition,
          width: deviceInfo.definition * deviceInfo.rate
        })
        recoverVideosAfterSetting()
      }
    }

    // 等待本地音视频轨道创建完成（getDevices 是异步建轨）。最多等 timeout，超时返回当前状态。
    const ensureLocalTracks = async (timeout = 8000) => {
      const start = Date.now()
      while (
        (!localAudioTrack.value || !localVideoTrack.value) &&
        Date.now() - start < timeout
      ) {
        await new Promise((r) => setTimeout(r, 100))
      }
      return !!(localAudioTrack.value && localVideoTrack.value)
    }

    const joinRoom = async () => {
      const { id } = route.params
      joinLoading.value = true
      const data = await serve['invite-guests'].getInviteGuestsToken({
        data: { activityId: id, nick: name.value }
      })
      joinLoading.value = false
      if (data === serve.FAIL) {
        return
      }

      if (!data) {
        emit('joinFail')
        return
      }
      joinLoading.value = true

      await client
        .value!.join(process.env.VUE_APP_RTC_APPID, id, data.token, data.uid)
        .catch((err) => {
          console.log(err)
          Message.error(t('room.preview.joinFail') as string)
          throw err
        })
        .finally(() => {
          joinLoading.value = false
        })

      // 记录自己的 uid，user-published 里用它跳过自己（防 uid 回环听到自己）
      joinedUid.value = data.uid

      isJoined.value = true
      await nextTick()

      // 本地音视频轨道是 getDevices 异步创建的，用户可能在建轨完成前就点了 Join。
      // 发布前先等轨道就绪，否则会 publish 到 undefined 导致"别人听不到你"。
      await ensureLocalTracks()

      localVideoTrack.value?.play('meVideo', {
        fit: 'contain'
      })
      console.log(localAudioTrack.value, 'localAudioTrack')

      const tracks = [localAudioTrack.value, localVideoTrack.value].filter(
        Boolean
      )
      if (!tracks.length) {
        Message.error(t('room.preview.joinFail') as string)
      } else {
        try {
          await client.value!.publish(tracks as any)
        } catch (err) {
          // 偶发失败重试一次，避免一次抖动就导致整场推不出声音/画面
          console.warn('[join] 发布失败，重试一次', err)
          try {
            await client.value!.publish(tracks as any)
          } catch (err2) {
            console.error('[join] 发布重试仍失败', err2)
            Message.error(t('room.preview.joinFail') as string)
          }
        }
      }

      // 推流诊断（验证完可删）
      try {
        setTimeout(() => {
          dumpDiagnostics().catch((e) =>
            console.warn('[dumpDiagnostics] 异步失败', e)
          )
        }, 1500)
        startStatsMonitor()
      } catch (e) {
        console.warn('[诊断启动] 失败，已忽略', e)
      }
    }

    // 注销：关闭本地音视频轨道并离开频道。幂等，避免重复 leave。
    let hasLeft = false
    const leaveChannel = () => {
      if (hasLeft) {
        return
      }
      hasLeft = true
      try {
        localVideoTrack.value?.close()
        localAudioTrack.value?.close()
      } catch (e) {
        console.warn('[leave] 关闭本地轨道失败', e)
      }
      try {
        client.value?.leave()
      } catch (e) {
        console.warn('[leave] 离开频道失败', e)
      }
    }

    // 浏览器刷新/关闭页签时，Vue 的 onBeforeUnmount 不一定触发，
    // 用 pagehide/beforeunload 兜底主动注销，让其它用户立即收到 user-left，
    // 不必等 Agora 超时（否则就会出现"某人刷新后别人音频乱掉"）。
    const handlePageHide = () => {
      leaveChannel()
    }

    const leaveRoom = async () => {
      leaveChannel()
      window.location.href = 'https://www.yololiv.com/'
      isJoined.value = false
    }

    // const handleMute = () => {
    //   const { id } = route.params
    //   const data = serve['invite-guests'].muteAudio({
    //     data: {
    //       activityId: id,
    //       muteAudio: host.value?._trust_audio_mute_state_ ? 0 : 1,
    //       uid: host.value.uid
    //     }
    //   })
    // }

    // 浏览器是否支持选择音频输出设备（setSinkId）。
    // 安卓 Chrome（含三星）不支持，强行 setPlaybackDevice 会让远端音频静音。
    const canSelectAudioOutput = () =>
      typeof (HTMLMediaElement.prototype as any).setSinkId === 'function'

    const switchSpeaker = (speakerId) => {
      // speakerId 为空或平台不支持时直接跳过，避免把远端音频切到非法输出导致听不到声音
      if (!speakerId || !canSelectAudioOutput()) {
        return
      }
      audioTrackList.value.forEach((track) => {
        track.setPlaybackDevice(speakerId).catch((e) => {
          console.warn('[switchSpeaker] setPlaybackDevice 失败', e)
        })
      })
    }

    onMounted(() => {
      client.value = AgoraRTC.createClient({
        codec: 'vp8', // 从vp8修改为h264
        mode: 'live',
        role: 'host'
      })
      // client.value.setLowStreamParameter({
      //   width: 160,
      //   height: 120,
      //   framerate: 15,
      //   bitrate: 120,
      // });

      // 开启双流模式。
      client.value
        .enableDualStream()
        .then(() => {
          console.log('enable dual stream success')
        })
        .catch((err) => {
          console.log(err)
        })

      client.value.on('exception', function (evt) {
        console.log(evt.code, evt.msg, evt.uid)
      })

      AgoraRTC.onMicrophoneChanged = async (changedDevice) => {
        const devices = await AgoraRTC.getMicrophones()
        deviceList.micDevices = devices
        console.log(devices, 'mic')
        const selectDevice = devices.find(
          (device) => device.deviceId === deviceInfo.microphoneId
        )
        deviceInfo.micName = devices[0].label || 'default Mic'

        if (changedDevice.state === 'ACTIVE') {
          return
        }

        // 拔出设备为当前设备时，切换到一个已有的设备。
        if (changedDevice.state === 'INACTIVE') {
          if (selectDevice) {
            return
          }

          if (devices[0]) {
            localAudioTrack.value?.setDevice(devices[0].deviceId)
            deviceInfo.microphoneId = devices[0].deviceId
            return
          }
          deviceInfo.micName = 'Without a Mic'
          deviceInfo.microphoneId = ''
        }
      }

      AgoraRTC.onCameraChanged = async (changedDevice) => {
        const devices = await AgoraRTC.getCameras()
        deviceList.videoDevices = devices
        console.log(devices, 'camera')
        if (changedDevice.state === 'ACTIVE') {
          return
        }
        // 拔出设备为当前设备时，切换到一个已有的设备。
        if (changedDevice.state === 'INACTIVE') {
          const selectDevice = devices.find(
            (device) => device.deviceId === deviceInfo.cameraId
          )

          if (selectDevice) {
            return
          }

          if (devices[0]) {
            localVideoTrack.value?.setDevice(devices[0].deviceId)
            deviceInfo.cameraId = devices[0].deviceId
            return
          }
          deviceInfo.cameraId = ''
        }
      }

      AgoraRTC.onPlaybackDeviceChanged = async (changedDevice) => {
        const devices = await AgoraRTC.getPlaybackDevices()
        console.log(devices, 'devices')
        deviceList.speakerDevices = devices

        if (changedDevice.state === 'ACTIVE') {
          return
        }
        // 拔出设备为当前设备时，切换到一个已有的设备。
        if (changedDevice.state === 'INACTIVE') {
          const selectDevice = devices.find(
            (device) => device.deviceId === deviceInfo.cameraId
          )

          if (selectDevice) {
            return
          }
          if (devices[0]) {
            deviceInfo.speakerId = devices[0].deviceId
            switchSpeaker(devices[0].deviceId)
            return
          }
          deviceInfo.speakerId = ''
        }
      }

      client.value.on('user-published', async (user, mediaType) => {
        // uid 回环/冲突时，可能把自己的轨道当成"远端"推回来——跳过自己，
        // 否则会订阅并播放自己的麦克风，导致从扬声器听到自己。
        if (joinedUid.value != null && user.uid === joinedUid.value) {
          console.warn('[user-published] 跳过自己的 uid（回环）', user.uid)
          return
        }

        // 只有主播（导播台 uid===1）才作为 host，供主画面/统计/恢复使用，
        // 访客发布不覆盖 host，避免重连后 host 指向已离开的访客。
        if (user.uid === 1) {
          host.value = user
        }

        // 订阅失败（如重连瞬间状态冲突）单独兜住，避免未处理的 reject 影响其它逻辑
        try {
          await client.value!.subscribe(user, mediaType)
        } catch (e) {
          console.warn('[user-published] subscribe 失败', user.uid, mediaType, e)
          return
        }

        // 音频：所有远端用户都播（访客需听到主播+其他访客，自己的本地轨道不在远端列表里）
        if (mediaType === 'audio') {
          const audioTrack = user.audioTrack
          if (!audioTrack) {
            return
          }
          // 维护远端音频列表：同 uid 先移除旧的，避免重连后重复堆叠；
          // 这同时让 switchSpeaker 能真正遍历到在播的远端音频。
          audioTrackList.value = audioTrackList.value.filter(
            (t) => t.getUserId() !== user.uid
          )
          audioTrackList.value.push(audioTrack)
          try {
            audioTrack.play()
            audioTrack.setVolume(200)
            // 仅在「平台支持 setSinkId 且有合法 speakerId」时切输出设备，
            // 安卓 Chrome 不支持，强行调用会让该路远端音频静音。
            if (deviceInfo.speakerId && canSelectAudioOutput()) {
              audioTrack.setPlaybackDevice(deviceInfo.speakerId).catch((e) => {
                console.warn('[user-published] setPlaybackDevice 失败', e)
              })
            }
          } catch (e) {
            console.warn('[user-published] 远端音频播放失败', user.uid, e)
          }
        }

        if (mediaType === 'video') {
          if (user.uid !== 1) {
            return
          }
          client.value!.setRemoteVideoStreamType(
            user.uid,
            RemoteStreamType.HIGH_STREAM
          )
          user.videoTrack?.play('hostVideo', {
            fit: 'contain'
          })
        }
      })

      // 取消发布：把对应远端音频从列表移除（SDK 会自动停止该远端轨道的播放）
      client.value.on('user-unpublished', (user, mediaType) => {
        if (mediaType === 'audio') {
          audioTrackList.value = audioTrackList.value.filter(
            (t) => t.getUserId() !== user.uid
          )
        }
      })

      // 用户离开（含刷新/断线）：清理其音频，主播离开则清空 host，
      // 重连时会重新走 user-published，保证状态对称、不残留。
      client.value.on('user-left', (user) => {
        audioTrackList.value = audioTrackList.value.filter(
          (t) => t.getUserId() !== user.uid
        )
        if (host.value?.uid === user.uid) {
          host.value = undefined
        }
      })

      const mqttTopic = 'inviteAudio/' + route.params.id
      const mqttMuteType = 'mute-audio'
      console.log('进入了mqtt注册', mqttTopic, mqttMuteType)

      mqtt.on(mqttTopic, mqttMuteType, async (data: string) => {
        console.log('mqtt mute-audio raw', data)
        try {
          const { data: info } = JSON.parse(data)
          console.log('mqtt mute-audio', info)
          if (info.operate === 0) {
            // 解禁：重新发布音频轨道
            if (localAudioTrack.value && client.value) {
              // if (localAudioTrack.value?.muted) {
              //   await localAudioTrack.value?.setMuted(false)
              // }

              await client.value.unpublish([localAudioTrack.value])
              await client.value.publish([localAudioTrack.value])
              console.log('audio track republished')
            }
          }
        } catch (e) {
          console.log('mqtt mute-audio error', e)
        }
      })

      // 刷新/关闭页签时主动注销（移动端 Safari 用 pagehide 更可靠）
      window.addEventListener('pagehide', handlePageHide)
      window.addEventListener('beforeunload', handlePageHide)

      getDevices()
      openLog()
    })

    onBeforeUnmount(() => {
      // SPA 路由离开时也注销并移除监听
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('beforeunload', handlePageHide)
      leaveChannel()

      // 必须与注册时的 topic 一致（注册用的是 'inviteAudio/<id>'），否则取消订阅匹配不上
      const mqttTopic = 'inviteAudio/' + route.params.id
      mqtt.off(mqttTopic, 'mute-audio')

      // 清理诊断 timer（验证完可删）
      try {
        if (statsTimer.value) {
          clearInterval(statsTimer.value)
          statsTimer.value = null
        }
        delete (window as any).__roomDiag
      } catch (e) {
        console.warn('[诊断清理] 失败，已忽略', e)
      }
    })

    return () => {
      return (
        <div class={styles.preview}>
          {/* <div onClick={handleMute}>禁言</div> */}
          {isJoined.value ? (
            <div class={styles.onLine}>
              <Camera className={styles.host} id="hostVideo" />
              <Camera className={styles.mine} id="meVideo">
                <div class={styles.username}>
                  <img src={nameImg} />
                </div>
              </Camera>
            </div>
          ) : (
            <div>
              <div class={styles.title}>{t('room.preview.title')}</div>
              <div class={styles.showBox}>
                <Camera className={styles.testCamera} id="meVideo" />
                <div class={styles.volume}>
                  <div class={styles.micIcon}>
                    <img src={micImg} />
                  </div>
                  <div class={styles.volumeBar}>
                    <div class={styles.volume_outer}>
                      <div
                        class={styles.volume_inner}
                        style={{
                          height: localVolumeLevel.value * 157 + 'px'
                        }}
                      ></div>
                    </div>
                  </div>
                  <div class={styles.maxMic}>{deviceInfo.micName || 'Mic'}</div>
                </div>
              </div>
            </div>
          )}

          <div class={styles.control}>
            <ControlItem
              icon={localAudioTrack.value?.muted ? unmuteImg : muteImg}
              label={
                (localAudioTrack.value?.muted
                  ? t('room.preview.Unmute')
                  : t('room.preview.Mute')) as string
              }
              onClick={switchMute}
            />
            <ControlItem
              icon={localVideoTrack.value?.muted ? noCamImg : camImg}
              label={
                (localVideoTrack.value?.muted
                  ? t('room.preview.OpenCam')
                  : t('room.preview.StopCam')) as string
              }
              onClick={switchCamera}
            />
            <ControlItem
              icon={settingsImg}
              label={t('room.preview.Settings') as string}
              onClick={openSetting}
            />
            {isJoined.value && (
              <ControlItem
                icon={leaveImg}
                label={t('room.preview.Leave') as string}
                onClick={leaveRoom}
              />
            )}
          </div>
          {!isJoined.value && (
            <div class={styles.form}>
              <div class={styles.nameInput}>
                <p class={styles.nameInputLabel}>{t('room.preview.guestLabel')}</p>
                <el-input
                  v-model={name.value}
                  class={styles.nameInputValue}
                ></el-input>
              </div>
              <div
                class={styles.action}
                v-loading={joinLoading.value}
                onClick={joinRoom}
              >
                {t('room.preview.join')}
              </div>
            </div>
          )}

          <SettingDialog
            deviceList={deviceList}
            visible={showSetting.value}
            value={deviceInfo}
            onConfirm={handleConfirm}
            onClose={handleCancelSetting}
          />
        </div>
      )
    }
  }
})
